import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PresentationSection from './PresentationSection';
import SecuritySection from './SecuritySection';
import SubscriptionSection from './SubscriptionSection';
import MyVideosSection from './MyVideosSection';
import { User, Shield, CreditCard, Play } from 'lucide-react';
import { normalizeRole, getRoleLabel, isCreator, debugRole } from '../../utils/roleUtils';

const ProfilePage = () => {
  const { user, userProfile, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('presentation');

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black pt-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Accès refusé</h1>
          <p className="text-gray-400">Vous devez être connecté pour accéder à cette page.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'presentation',
      name: 'Présentation',
      icon: User,
      component: PresentationSection
    },
    {
      id: 'my-videos',
      name: 'Mes Vidéos',
      icon: Play,
      component: MyVideosSection,
      creatorOnly: false // Maintenant visible pour tous les utilisateurs
    },
    {
      id: 'security',
      name: 'Sécurité',
      icon: Shield,
      component: SecuritySection
    },
    {
      id: 'subscription',
      name: 'Abonnement',
      icon: CreditCard,
      component: SubscriptionSection
    }
  ];

  // Filtrer les onglets selon le rôle utilisateur
  const visibleTabs = tabs.filter(tab => 
    !tab.creatorOnly || (userProfile?.role === 'creator')
  );

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || PresentationSection;

  // Normaliser le rôle et debug
  const normalizedRole = normalizeRole(userProfile?.role);
  const userIsCreator = isCreator(userProfile?.role);
  const roleLabel = getRoleLabel(userProfile?.role);

  // Debug: log du rôle dans ProfilePage sidebar
  debugRole(userProfile?.role, 'ProfilePage Sidebar');

  return (
    <div className="min-h-screen bg-black pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar avec onglets */}
          <div className="lg:w-1/4">
            <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800 sticky top-24">
              <div className="text-center mb-6">
                <img
                  src={userProfile?.avatar_url || user?.photoURL || '/api/placeholder/150/150'}
                  alt="Avatar"
                  className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-pink-500"
                  onError={(e) => {
                    e.target.src = '/api/placeholder/150/150';
                  }}
                />
                <h2 className="text-xl font-bold text-white">
                  {userProfile?.username || user?.displayName || 'Utilisateur'}
                </h2>
                <p className="text-gray-400 text-sm">
                  {userProfile?.email || user?.email}
                </p>
                <div className="mt-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    userIsCreator 
                      ? 'bg-yellow-600/30 text-yellow-300 border border-yellow-500/30' 
                      : 'bg-blue-600/30 text-blue-300 border border-blue-500/30'
                  }`}>
                    {roleLabel}
                  </span>
                </div>
              </div>

              {/* Navigation des onglets */}
              <nav className="space-y-2">
                {visibleTabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      data-tab={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-pink-600/20 text-pink-400 border border-pink-500/30'
                          : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                      }`}
                    >
                      <IconComponent className="h-5 w-5" />
                      <span className="font-medium">{tab.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="lg:w-3/4">
            <div className="bg-gray-900/50 rounded-2xl p-8 border border-gray-800">
              <ActiveComponent />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;